/**
 * Taxes (Overall > Finance).
 *
 * Fan, 2026-10-28: "The Overview tab should feature one chart for tax debt,
 * one chart for tax reserves, one chart for taxes paid. The France tab
 * should feature: Overview (the same three charts), By entity (breakdowns
 * and drop downs by month and tax type) and Tax returns." UK mirrors France.
 *
 * Fan, 2026-10-27 (round 3): "It should look more like the Property
 * Portfolio scope". Same layout as that page: All / France / UK pills, year
 * switcher, GBP / EUR toggle, KPI cards, a monthly chart, a monthly table,
 * a by-company table, plus the VAT returns. The earlier detail views
 * (reserves breakdown, debts by tax, paid lines) stay underneath as tabs
 * (?tab=reserves|debts|paid) so nothing is lost.
 *
 * Entities and Personal scopes (Fan, 2026-10-29: "replicate just the page
 * Taxes from the Overall scope adapted to the entities and personal"): the
 * same page via <ScopedTaxPage scope="entities|personal" />, narrowed to
 * that scope's entities (and to the sidebar pick when one entity or one
 * country is selected). The old Tax Reserves page (/tax) keeps its reserve
 * settings and is linked from the bottom of the scoped page.
 *
 * Read-only: figures come from /tax-reserves/group-summary, /payroll/overview,
 * /reports/tax-paid and /payroll/vat-returns; lib/taxOverview.js only adds
 * them up and converts with the report's own FX rates (labelled approx).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { TaxReservesGroupSummary } from '../components/TaxReservesGroupSummary';
import { PayrollTaxDebtsPanel } from '../components/PayrollTaxDebtsPanel';
import { TaxPaidPanel } from '../components/TaxPaidPanel';
import { TaxReturnsTable } from '../components/tax/TaxReturnsTable';
import { PaymentPlansTable } from '../components/tax/PaymentPlansTable';
import {
  KpiCard, YearNav, PillTabs, SectionCard, MonthlyStackedChart, fmtMoney,
} from '../components/tax/TaxDashboardParts';
import { reportsAPI, payrollAPI, taxReservesGroupAPI, brosTaxReservesAPI, entitiesAPI } from '../lib/api';
import { usePortfolioPrefsStore, useEntityStore } from '../store';
import { buildTaxOverview, fxFromRates, makeDisplay, makeToGbp, MONTH_LABELS, TAX_KINDS } from '../lib/taxOverview';
import { cn } from '../lib/utils';
import { ROUTES } from '../lib/routeConstants';

const TABS = ['reserves', 'debts', 'paid'];
const TOP_OPTIONS = [
  { key: 'overview', label: 'Overview' },
  { key: 'fr', label: '\uD83C\uDDEB\uD83C\uDDF7 France' },
  { key: 'uk', label: '\uD83C\uDDEC\uD83C\uDDE7 UK' },
];
const SUB_OPTIONS = [
  { key: 'overview', label: 'Overview' },
  { key: 'entities', label: 'By entity' },
  { key: 'returns', label: 'Tax returns' },
];
const RESERVE_SERIES = [
  { key: 'vat', label: 'VAT vault', color: '#3B82F6' },
  { key: 'corporation_tax', label: 'Corporation tax vault', color: '#8B5CF6' },
];
const FLAG = { uk: '\uD83C\uDDEC\uD83C\uDDE7', fr: '\uD83C\uDDEB\uD83C\uDDF7' };
const REFUND_SERIES = { key: 'refunds', label: 'Refunds', color: '#EF4444' };

function makeFmt(currency) {
  return (v) => fmtMoney(v, currency || 'GBP');
}

function fmtDate(d) {
  if (!d) return '-';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  return dd ? `${dd}/${m}/${y}` : d;
}


function TaxCharts({ view, show, dc, money, personal = false }) {
  const paidSeries = [...view.usedKinds, REFUND_SERIES];
  const paidData = view.months.map((m) => {
    const row = { label: m.label };
    paidSeries.forEach((s) => { row[s.key] = show(m[s.key]) || 0; });
    return row;
  });
  // What is really in the tax vaults at each month end (Fan, 2026-10-28),
  // not the calculated reserve (that stays in the KPI card and By entity).
  const reserveData = view.vaultMonths.map((m) => ({
    label: m.label,
    vat: m.vat == null ? null : show(m.vat),
    corporation_tax: m.corporation_tax == null ? null : show(m.corporation_tax),
  }));
  const hasVaults = view.vaultAccounts.length > 0;
  const debtData = view.debtChart.rows.map((r) => {
    const row = { label: r.label };
    view.debtChart.kinds.forEach((k) => { row[k.key] = show(r[k.key] || 0) || 0; });
    return row;
  });
  return (
    <>
      <SectionCard title={`Tax debt (${dc} approx)`} subtitle={personal ? 'What each person still owes, by tax.' : 'What each company still owes, by tax.'} testId="tax-chart-debt">
        {debtData.length === 0
          ? <p className="text-sm text-muted-foreground">Nothing owed.</p>
          : <MonthlyStackedChart data={debtData} series={view.debtChart.kinds} ccy={dc} height={260} />}
      </SectionCard>
      <SectionCard
        title={`Tax reserves in vaults (${dc} approx)`}
        subtitle={hasVaults
          ? `Balance of the VAT and corporation tax vaults at each month end, this month live. Should be ${money(view.kpis.shouldBe)}.`
          : (personal
            ? `No tax vault held personally. Should be ${money(view.kpis.shouldBe)}.`
            : `No VAT or corporation tax vault for these companies. Should be ${money(view.kpis.shouldBe)}.`)}
        testId="tax-chart-reserves"
      >
        {hasVaults
          ? <MonthlyStackedChart data={reserveData} series={RESERVE_SERIES} ccy={dc} height={260} />
          : <p className="text-sm text-muted-foreground">Nothing set aside in a vault.</p>}
        {hasVaults && <VaultCheck moves={view.vaultMoves} money={money} />}
      </SectionCard>
      <SectionCard title={`Taxes paid (${dc} approx)`} subtitle="Bank payments to tax offices, by tax. Refunds show below zero." testId="tax-chart-paid">
        <MonthlyStackedChart data={paidData} series={paidSeries} ccy={dc} height={260} />
      </SectionCard>
    </>
  );
}

// Put in vs taken out per vault (Fan, 2026-10-29). Put in = the app's own
// reserve transfers; taken out = put in minus what is there now.
function VaultCheck({ moves, money }) {
  const rows = (moves || []).filter((a) => a.balanceGbp || a.putInGbp || a.takenOutGbp);
  if (!rows.length) return null;
  const tot = (f) => rows.reduce((s, a) => s + (a[f] || 0), 0);
  return (
    <div className="mt-4" data-testid="tax-vault-check">
      <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Put in and taken out</p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Vault</TableHead>
            <TableHead className="text-right">Put in by the app</TableHead>
            <TableHead className="text-right">Taken out</TableHead>
            <TableHead className="text-right">In now</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((a) => (
            <TableRow key={a.account_id}>
              <TableCell className="text-xs">{a.name}</TableCell>
              <TableCell className="text-right font-mono text-xs">{a.putInGbp == null ? <span title="Not filled by the app">-</span> : money(a.putInGbp)}</TableCell>
              <TableCell className={cn('text-right font-mono text-xs', (a.takenOutGbp || 0) > 0.005 && 'text-red-600 font-semibold')}>
                {a.takenOutGbp == null ? '-' : money(a.takenOutGbp)}
              </TableCell>
              <TableCell className="text-right font-mono text-xs">{money(a.balanceGbp)}</TableCell>
            </TableRow>
          ))}
          <TableRow className="font-semibold border-t-2">
            <TableCell className="text-xs">Total</TableCell>
            <TableCell className="text-right font-mono text-xs">{money(tot('putInGbp'))}</TableCell>
            <TableCell className="text-right font-mono text-xs">{money(tot('takenOutGbp'))}</TableCell>
            <TableCell className="text-right font-mono text-xs">{money(tot('balanceGbp'))}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function TaxKpis({ view, money, region, year, personal = false }) {
  const k = view.kpis;
  const splitLine = (field) => (region === 'all'
    ? `${FLAG.uk} ${money(view.split.uk[field])} / ${FLAG.fr} ${money(view.split.fr[field])}`
    : null);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tax-kpis">
      <KpiCard label="Owed" value={money(k.owed)} subDetail={splitLine('owed')} tooltip="Tax debts still to pay: notices, payment plans, monthly PAYE" />
      <KpiCard label="Overdue" value={money(k.overdue)} tone={k.overdue > 0 ? 'bad' : undefined} subDetail={splitLine('overdue') || (k.nextDue ? `Next due ${fmtDate(k.nextDue)}` : null)} />
      <KpiCard
        label="In tax vaults"
        value={money(view.vaultNow.total)}
        tone={k.gap < -0.005 ? 'bad' : undefined}
        subDetail={`Should be ${money(k.shouldBe)}. Gap ${money(k.gap)}`}
        tooltip={`Live balance of the VAT and corporation tax vaults. Should be = tax worked out on this year's payments minus that tax already paid this year. Gap below zero = money taken out or never put in (By entity tab, per ${personal ? 'person' : 'company'}).`}
      />
      <KpiCard
        label={`Paid in ${year}`}
        value={money(k.paid)}
        subDetail={k.refunds > 0 || k.offsetRefunds > 0
          ? `Refunds ${money(k.refunds)}${k.offsetRefunds > 0 ? `, + ${money(k.offsetRefunds)} VAT refunds set off by HMRC` : ''}`
          : splitLine('paid')}
        tooltip="Payments to tax offices, read from the bank lines"
      />
    </div>
  );
}

function KindMonths({ ks, money, nativeFmt, nowMonthIdx }) {
  const rows = ks.months.map((m, i) => ({ ...m, i })).filter((m) => m.reserved || m.paid || m.refunds || m.due);
  return (
    <div className="pl-8 py-2 space-y-3">
      {rows.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              <TableHead className="text-right">Set aside (calc.)</TableHead>
              <TableHead className="text-right">Falls due</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Refunds</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((m) => (
              <TableRow key={m.i} className={cn(m.i === nowMonthIdx && 'bg-emerald-50/60 dark:bg-emerald-950/20')}>
                <TableCell className="text-xs">{MONTH_LABELS[m.i]}</TableCell>
                <TableCell className="text-right font-mono text-xs">{m.reserved ? money(m.reserved) : '-'}</TableCell>
                <TableCell className="text-right font-mono text-xs">{m.due ? money(m.due) : '-'}</TableCell>
                <TableCell className="text-right font-mono text-xs">{m.paid ? money(m.paid) : '-'}</TableCell>
                <TableCell className="text-right font-mono text-xs text-emerald-700">{m.refunds ? money(m.refunds) : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {ks.debts.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Still to pay</p>
          <ul className="text-xs space-y-1">
            {ks.debts.map((o) => (
              <li key={o.id} className="flex justify-between gap-3">
                <span>
                  {o.label}
                  {o.state === 'overdue' && <Badge variant="outline" className="ml-2 border-red-300 text-red-700">Overdue</Badge>}
                  <span className="text-muted-foreground ml-2">{o.due_date ? `due ${fmtDate(o.due_date)}` : 'no dates yet'}</span>
                </span>
                <span className="font-mono whitespace-nowrap">{nativeFmt(o.amount, o.currency)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {ks.paidLines.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Paid and refunded</p>
          <ul className="text-xs space-y-1">
            {ks.paidLines.map((ln) => (
              <li key={`${ln.transaction_id}-${ln.role}`} className="flex justify-between gap-3">
                <span className="truncate" title={ln.label}>
                  {fmtDate(ln.date)} <span className="text-muted-foreground">{ln.authority}</span>
                  {ln.role === 'refund' && <Badge variant="outline" className="ml-2 border-emerald-300 text-emerald-700">Refund</Badge>}
                </span>
                <span className="font-mono whitespace-nowrap">{nativeFmt(Math.abs(Number(ln.amount)), ln.currency)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ByEntity({ view, money, nativeFmt, year, nowMonthIdx, personal = false }) {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (id) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const k = view.kpis;
  const cells = (o, cls = '') => (
    <>
      <TableCell className={cn('text-right font-mono', cls)}>{o.owed ? money(o.owed) : '-'}</TableCell>
      <TableCell className={cn('text-right font-mono', cls, o.overdue > 0 && 'text-red-600 font-semibold')}>{o.overdue ? money(o.overdue) : '-'}</TableCell>
      <TableCell className={cn('text-right font-mono', cls)}>{o.shouldBe ? money(o.shouldBe) : '-'}</TableCell>
      <TableCell className={cn('text-right font-mono', cls)}>{o.vault ? money(o.vault) : '-'}</TableCell>
      <TableCell className={cn('text-right font-mono', cls, (o.gap || 0) < -0.005 && 'text-red-600 font-semibold')}>
        {o.shouldBe || o.vault ? money(o.gap || 0) : '-'}
      </TableCell>
      <TableCell className={cn('text-right font-mono', cls)}>{o.paid ? money(o.paid) : '-'}</TableCell>
      <TableCell className={cn('text-right font-mono text-emerald-700', cls)}>{o.refunds ? money(o.refunds) : '-'}</TableCell>
    </>
  );
  return (
    <SectionCard title="By entity" subtitle={`Click a ${personal ? 'person' : 'company'} to see each tax, then a tax to see it month by month.`} testId="tax-by-company">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{personal ? 'Person / tax' : 'Company / tax'}</TableHead>
            <TableHead className="text-right">Owed</TableHead>
            <TableHead className="text-right">Overdue</TableHead>
            <TableHead className="text-right" title="Tax worked out on this year's payments, minus that tax already paid this year">Should be in vault</TableHead>
            <TableHead className="text-right" title={`Live balance of the ${personal ? "person's" : "company's"} VAT and corporation tax vaults`}>In vault</TableHead>
            <TableHead className="text-right" title="In vault minus should be. Red = money taken out or never put in">Gap</TableHead>
            <TableHead className="text-right">{`Paid in ${year}`}</TableHead>
            <TableHead className="text-right">Refunds</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {view.companies.map((c) => {
            const isOpen = open.has(c.id);
            return (
              <React.Fragment key={c.id}>
                <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggle(c.id)}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-1">
                      {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      {c.name}
                    </span>
                  </TableCell>
                  {cells(c)}
                </TableRow>
                {isOpen && c.kindList.map((ks) => {
                  const kid = `${c.id}|${ks.key}`;
                  const kOpen = open.has(kid);
                  const color = (TAX_KINDS.find((t) => t.key === ks.key) || {}).color;
                  return (
                    <React.Fragment key={kid}>
                      <TableRow className="cursor-pointer bg-muted/20 hover:bg-muted/40" onClick={() => toggle(kid)}>
                        <TableCell className="pl-8 text-sm">
                          <span className="inline-flex items-center gap-1.5">
                            {kOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
                            {ks.label}
                          </span>
                        </TableCell>
                        {cells(ks, 'text-sm')}
                      </TableRow>
                      {kOpen && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={8}>
                            <KindMonths ks={ks} money={money} nativeFmt={nativeFmt} nowMonthIdx={nowMonthIdx} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
          <TableRow className="font-semibold border-t-2">
            <TableCell>Total</TableCell>
            {cells(k)}
          </TableRow>
        </TableBody>
      </Table>
    </SectionCard>
  );
}

function TaxReturns({ view, nativeFmt, year, regionLabel, conv, dc }) {
  return (
    <div className="flex flex-col gap-6">
      <TaxReturnsTable returns={view.taxItems || view.vatReturns} year={year} nativeFmt={nativeFmt} regionLabel={regionLabel} conv={conv} dc={dc} />
    </div>
  );
}

function DetailTabs({ data }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [shown, setShown] = useState(Boolean(tabParam));
  const tab = TABS.includes(tabParam) ? tabParam : 'reserves';
  const setTab = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };
  return (
    <div>
      <button type="button" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" onClick={() => setShown((v) => !v)}>
        {shown ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        Full lists (how each reserve is worked out, every debt, every payment)
      </button>
      {shown && (
        <Tabs value={tab} onValueChange={setTab} className="mt-3">
          <TabsList>
            <TabsTrigger value="reserves" data-testid="overall-tax-tab-reserves">Tax reserves</TabsTrigger>
            <TabsTrigger value="debts" data-testid="overall-tax-tab-debts">Tax debts</TabsTrigger>
            <TabsTrigger value="paid" data-testid="overall-tax-tab-paid">Tax paid</TabsTrigger>
          </TabsList>
          <TabsContent value="reserves" className="mt-4">
            {tab === 'reserves' && <TaxReservesGroupSummary />}
          </TabsContent>
          <TabsContent value="debts" className="mt-4">
            {tab === 'debts' && <PayrollTaxDebtsPanel show="debts" groupByCompany />}
          </TabsContent>
          <TabsContent value="paid" className="mt-4">
            {tab === 'paid' && (
              data.taxPaid
                ? <TaxPaidPanel taxPaid={data.taxPaid} fmt={makeFmt(data.taxPaid?.currency)} groupByEntity />
                : <p className="text-sm text-muted-foreground">No tax paid figures.</p>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

const COUNTRY_OF_GROUP = { 'group:france': 'France', 'group:uk': 'UK', 'group:personal': 'UK/France' };

/**
 * Which entity ids (the short codes the tax figures use, e.g. 'eci') belong
 * to a scope. Entities = every non-personal entity, Personal = group_role
 * personal, same split as the sidebar picker. A single entity or a country
 * group picked in the sidebar narrows it further. Returns null for Overall.
 */
export function scopeEntityCodes(entities, scope, selectedEntity) {
  if (scope !== 'entities' && scope !== 'personal') return null;
  const list = (entities || []).filter((e) => (scope === 'personal'
    ? e.group_role === 'personal'
    : e.group_role !== 'personal'));
  const codeOf = (e) => e.type || e.id;
  let picked = list;
  if (selectedEntity && selectedEntity !== 'all') {
    if (String(selectedEntity).startsWith('group:')) {
      const country = COUNTRY_OF_GROUP[selectedEntity];
      if (country) picked = list.filter((e) => e.country === country);
      else if (selectedEntity === 'group:other') picked = list.filter((e) => !Object.values(COUNTRY_OF_GROUP).includes(e.country));
    } else {
      const one = list.filter((e) => e.id === selectedEntity || e.type === selectedEntity);
      if (one.length) picked = one;
    }
  }
  return {
    codes: new Set(picked.map(codeOf)),
    label: picked.length === 1 && picked.length !== list.length ? picked[0].name : null,
  };
}

const SCOPE_TEXT = {
  overall: { title: 'Taxes', subtitle: 'What is owed, what is set aside and what was paid' },
  entities: { title: 'Taxes', subtitle: 'What the companies owe, set aside and paid' },
  personal: { title: 'Taxes', subtitle: 'What is owed, set aside and paid personally' },
};

export function OverallTaxPage() {
  return <TaxesPage scope="overall" />;
}

export function ScopedTaxPage({ scope }) {
  return <TaxesPage scope={scope === 'personal' ? 'personal' : 'entities'} />;
}

function TaxesPage({ scope }) {
  const scoped = scope !== 'overall';
  const selectedEntity = useEntityStore((s) => s.selectedEntity);
  const [entityList, setEntityList] = useState(null);
  useEffect(() => {
    if (!scoped) return undefined;
    let alive = true;
    Promise.resolve().then(() => entitiesAPI.getAll())
      .then((r) => { if (alive) setEntityList(Array.isArray(r?.data) ? r.data : (r?.data?.entities || [])); })
      .catch(() => { if (alive) setEntityList([]); });
    return () => { alive = false; };
  }, [scoped]);
  const scopeSel = useMemo(
    () => (scoped && entityList ? scopeEntityCodes(entityList, scope, selectedEntity) : null),
    [scoped, entityList, scope, selectedEntity],
  );
  const entityFilter = useMemo(() => (scopeSel ? (id) => scopeSel.codes.has(id) : null), [scopeSel]);
  // Names for people/companies with a debt or payment but no reserve row.
  const entityNames = useMemo(() => {
    const out = {};
    (entityList || []).forEach((e) => { if (e.type) out[e.type] = e.name; });
    return out;
  }, [entityList]);

  const [searchParams, setSearchParams] = useSearchParams();
  const top = TOP_OPTIONS.some((o) => o.key === searchParams.get('view')) ? searchParams.get('view') : 'overview';
  const sub = SUB_OPTIONS.some((o) => o.key === searchParams.get('sub')) ? searchParams.get('sub') : 'overview';
  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true });
  };
  const region = top === 'overview' ? 'all' : top;
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const dc = usePortfolioPrefsStore((s) => s.displayCurrency) || 'GBP';
  const [data, setData] = useState({ groupSummary: null, countryMonths: null, debts: null, taxPaid: null, vatReturns: null, vaults: null, fxRates: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    const safe = (fn) => Promise.resolve().then(fn).then((r) => r?.data || null).catch(() => null);
    Promise.all([
      safe(() => taxReservesGroupAPI.getGroupSummary(year)),
      safe(() => brosTaxReservesAPI.getCountryMonths({ countries: 'all', year })),
      safe(() => payrollAPI.getOverview()),
      safe(() => reportsAPI.getTaxPaid({ year })),
      safe(() => payrollAPI.getVatReturns({ year })),
      safe(() => taxReservesGroupAPI.getVaultBalances(year)),
      safe(() => taxReservesGroupAPI.getFxRates()),
    ]).then(([groupSummary, countryMonths, debts, taxPaid, vatReturns, vaults, rates]) => {
      if (alive) setData({ groupSummary, countryMonths, debts, taxPaid, vatReturns, vaults, fxRates: fxFromRates(rates) });
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [year]);

  useEffect(() => load(), [load]);

  const view = useMemo(
    () => buildTaxOverview({ ...data, region, year, entityFilter, entityNames }),
    [data, region, year, entityFilter, entityNames],
  );
  const waitingForScope = scoped && !entityList;
  const text = SCOPE_TEXT[scope] || SCOPE_TEXT.overall;
  const show = useMemo(() => makeDisplay(view.fx, dc), [view.fx, dc]);
  const money = (gbp) => fmtMoney(show(gbp), dc);
  const nativeFmt = (v, ccy) => fmtMoney(v, ccy);
  // Native amount -> toggle currency (Tax returns tab, Fan 2026-10-30).
  const conv = useMemo(() => {
    const toGbp = makeToGbp(view.fx);
    return (v, ccy) => {
      const g = toGbp(v, ccy);
      return g === null ? null : show(g);
    };
  }, [view.fx, show]);
  const nowMonthIdx = year === thisYear ? new Date().getMonth() : -1;
  const regionLabel = region === 'fr' ? 'French' : region === 'uk' ? 'UK' : '';

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6" data-testid={scoped ? `${scope}-tax-page` : 'overall-tax-page'}>
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{text.title}{scopeSel?.label ? `: ${scopeSel.label}` : ''}</h1>
            <p className="text-muted-foreground mt-1">{text.subtitle}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <CurrencyToggle />
            <YearNav year={year} setYear={setYear} />
            <Button variant="outline" size="sm" onClick={load} aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <PillTabs value={top} onChange={(v) => setParam('view', v)} options={TOP_OPTIONS} testId="tax-region" />
        {top !== 'overview' && (
          <Tabs value={sub} onValueChange={(v) => setParam('sub', v)}>
            <TabsList data-testid="tax-sub-tabs">
              {SUB_OPTIONS.map((o) => <TabsTrigger key={o.key} value={o.key} data-testid={`tax-sub-${o.key}`}>{o.label}</TabsTrigger>)}
            </TabsList>
          </Tabs>
        )}

        {loading || waitingForScope ? (
          <Card><CardContent className="py-12 flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></CardContent></Card>
        ) : (
          <>
            {(top === 'overview' || sub === 'overview') && (
              <>
                <TaxKpis view={view} money={money} region={region} year={year} personal={scope === 'personal'} />
                <TaxCharts view={view} show={show} dc={dc} money={money} personal={scope === 'personal'} />
                <PaymentPlansTable plans={view.paymentPlans || []} nativeFmt={nativeFmt} regionLabel={regionLabel} />
              </>
            )}
            {top !== 'overview' && sub === 'entities' && (
              <ByEntity view={view} money={money} nativeFmt={nativeFmt} year={year} nowMonthIdx={nowMonthIdx} personal={scope === 'personal'} />
            )}
            {top !== 'overview' && sub === 'returns' && (
              <TaxReturns view={view} nativeFmt={nativeFmt} year={year} regionLabel={regionLabel} conv={conv} dc={dc} />
            )}
            {view.unconverted > 0 && (
              <p className="text-xs text-amber-700">
                {view.unconverted} amount(s) have no exchange rate and are left out of the converted totals.
              </p>
            )}
          </>
        )}

        {top === 'overview' && !scoped && <DetailTabs data={data} />}
        {scoped && scope === 'entities' && (
          <p className="text-xs text-muted-foreground" data-testid="tax-reserve-settings-link">
            Reserve rates, linked vault accounts and the VAT sync are on the{' '}
            <Link to={ROUTES.TAX_RESERVES} className="underline">Tax reserves settings</Link> page.
          </p>
        )}
      </div>
    </DashboardLayout>
  );
}

export default OverallTaxPage;
